import { AssistantTaskUpdate } from 'types/AssistantTypes';

// Structural shape of the SDK's TaskDetails as consumed here — only the
// fields the input-bar status actually reads.
export type ActiveTaskLike = {
  message?: string;
  title?: string;
  description?: string;
  is_completed?: boolean;
  progress?: number;
  current_step?: number;
  total_steps?: number;
};

export const REFINING_TEXT = 'Sto affinando l’analisi per garantire informazioni aggiornate e accurate.';

type DeriveTaskUpdateArgs = {
  runInProgress: boolean;
  pendingAction: unknown;
  latestActiveTask: ActiveTaskLike | undefined;
  messages: ReadonlyArray<{ role?: string }>;
  t: (key: string) => string;
};

export function deriveTaskUpdate({
  runInProgress,
  pendingAction,
  latestActiveTask,
  messages,
  t,
}: DeriveTaskUpdateArgs): AssistantTaskUpdate | undefined {
  // Checked BEFORE the runInProgress guard: a failed action submit (e.g. a
  // 400 validation error) makes the SDK drop isLoading while the action stays
  // pending, and the waiting state — with its input-bar cancel button — must
  // survive that, or the user can neither cancel nor see why the input is
  // still blocked.
  if (pendingAction) {
    return {
      update: t('assistant.status.waitingForYourInput'),
      status: 'pending',
    } as AssistantTaskUpdate;
  }
  if (!runInProgress) return undefined;

  // While the agent hasn't started responding yet (last message is the user's),
  // show the same continuous "Sto affinando…" text the chat skeleton bubble
  // shows. This avoids the input-bar text drifting (e.g. flashing the raw
  // "Pre-elaborazione" mid-run) and stays in sync with TaskStatuses' fallback.
  const lastMessage = messages[messages.length - 1];
  const agentNotStartedYet = lastMessage?.role === 'user';

  if (latestActiveTask) {
    let statusText =
      latestActiveTask.message ||
      latestActiveTask.title ||
      latestActiveTask.description ||
      t('assistant.status.processing');

    // Allow-listed tasks get their dedicated localized text.
    if (statusText === 'Pre-elaborazione.' || statusText === 'Preprocessing.') {
      statusText = REFINING_TEXT;
    } else if (statusText === 'Fineco Create Portfolio') {
      if (!latestActiveTask.is_completed) {
        statusText = 'Sto generando il portafoglio ottimizzato.';
      } else {
        statusText = 'Il portafoglio è stato creato con successo.';
      }
    } else if (statusText === 'Fineco Set Constraints') {
      if (!latestActiveTask.is_completed) {
        statusText = 'I vincoli sono stati impostati correttamente.';
      } else {
        statusText = 'I vincoli sono stati definiti con successo.';
      }
    } else if (agentNotStartedYet) {
      // Off-allow-list task running while agent still hasn't surfaced text:
      // keep the same continuous refining message rather than leaking the
      // raw English task name or generic 'processing' copy.
      statusText = REFINING_TEXT;
    } else {
      // Off-allow-list task while agent is mid-response: hide.
      return undefined;
    }

    return {
      update: statusText,
      status: latestActiveTask.is_completed ? 'completed' : 'pending',
      progress_percentage:
        typeof latestActiveTask.progress === 'number' ? Math.round(latestActiveTask.progress * 100) : undefined,
      detailed_message:
        typeof latestActiveTask.current_step === 'number' && typeof latestActiveTask.total_steps === 'number'
          ? `${t('assistant.tasks.step')} ${latestActiveTask.current_step}/${latestActiveTask.total_steps}`
          : undefined,
    } as AssistantTaskUpdate;
  }

  // No active task yet but a run is in progress. If agent hasn't surfaced any
  // text either, mirror the skeleton-bubble fallback. Otherwise keep the
  // generic processing copy.
  if (agentNotStartedYet) {
    return { update: REFINING_TEXT, status: 'pending' } as AssistantTaskUpdate;
  }
  return { update: t('assistant.status.processing'), status: 'pending' } as AssistantTaskUpdate;
}

export function isChatInputLocked(runInProgress: boolean, pendingAction: unknown): boolean {
  return runInProgress || !!pendingAction;
}
